import { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, TextField, Dialog, DialogTitle, DialogContent, IconButton, DialogActions, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import { checkCompanyName, postCompanyForm } from '../../../../api/companies';
import FormTextWithValue from '../../../../reusable-components/form/FormTextWithValue';
import FormDropdown from '../../../../reusable-components/form/FormDropdown';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { FIFTY_STATES } from '../../../../utils/dropdowns';
import { AwaitButton } from '../../../../reusable-components/await-button';

EditItemDialog.propTypes = {
  row: PropTypes.object,
  id: PropTypes.number,
  getGroups: PropTypes.func,
};

export default function EditItemDialog({ id, row, getGroups }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState({ ...row });
  const { enqueueSnackbar } = useSnackbar();

  const checkDuplicateItem = async () => {
    const response = await checkCompanyName(value.name, row.id);
    if (response.status === 200) {
      if (response.data === true) {
        enqueueSnackbar('Group  already exists', { variant: 'error' });
        return true;
      }
    }
    return false;
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async () => {
    const isDuplicate = await checkDuplicateItem();
    if (isDuplicate) return;

    const response = await postCompanyForm({ value });
    if (response.status === 200) {
      getGroups();
      enqueueSnackbar('Group updated successfully', { variant: 'success' });
      handleClose();
    } else {
      enqueueSnackbar('Error updating group', { variant: 'error' });
    }
  };

  return (
    <div>
      <Tooltip title="Edit">
        <IconButton color="secondary" onClick={handleClickOpen}>
          <EditIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
          }}
        >
          Edit item
          <CloseIconButton onClick={handleClose}/>
        </DialogTitle>
        <DialogContent sx={{ height: '100%', width: '100%' }}>
          <Grid container direction="row" justifyContent="center" alignItems="flex-start">
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              sx={{ mt: 0.5 }}
              spacing={2}
            >
              <FormTextWithValue
                value={value}
                title="Group Name*"
                flex={6}
                minWidth="350px"
                name="name"
                setValue={setValue}
                autoComplete="no-autocomplete"
                id={`edit-group-name-${row.id}`}
              />
              <FormTextWithValue
                value={value}
                title="Short Name"
                flex={6}
                minWidth="350px"
                name="shortName"
                setValue={setValue}
                autoComplete="off"
              />
            </Grid>

            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              sx={{ mt: 0.5 }}
              spacing={2}
            >
              <FormTextWithValue
                value={value}
                title="Address"
                flex={6}
                minWidth="350px"
                name="address"
                setValue={setValue}
                autoComplete="no-autocomplete"
                id={`edit-group-address-${row.id}`}
              />
              <FormTextWithValue
                value={value}
                title="City"
                flex={6}
                minWidth="350px"
                name="city"
                setValue={setValue}
                autoComplete="no-autocomplete"
                id={`edit-group-city-${row.id}`}
              />
            </Grid>

            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              sx={{ mt: 0.5 }}
              spacing={2}
            >
              <Grid item xs={6}>
                <FormDropdown
                  title="State"
                  fullWidth
                  value={
                    FIFTY_STATES.find((state) => state.value === value.state) || {
                      label: value.state,
                      value: value.state,
                    }
                  }
                  onChange={(event, newValue) => {
                    setValue({ ...value, state: newValue.value });
                  }}
                  options={FIFTY_STATES}
                  getOptionLabel={(option) => option?.label || ''}
                  autoComplete="no-autocomplete"
                  id={`edit-group-state-${row.id}`}
                  renderInput={(params) => <TextField {...params} label="Search or select" />}
                  flex={6}
                />
              </Grid>
              <FormTextWithValue
                value={value}
                title="Zip"
                flex={6}
                minWidth="350px"
                name="zip"
                setValue={setValue}
                autoComplete="no-autocomplete"
                id={`edit-group-zip-${row.id}`}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', mb: 2 }}>
          <AwaitButton
            disabled={value.name === ''}
            variant="contained"
            color="primary"
            sx={{ width: '200px', boxShadow: 3, fontWeight: 'bold' }}
            onClick={handleSave}
          >
            Save
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}
